import { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { gymApi } from '@/lib/api';
import { RewardAccessCode } from '@/types';
import { reportError } from '@/lib/sentry';
import { useToast } from '@/hooks/use-toast';
import { Check, Clock, Copy, KeyRound, Loader2, RefreshCw } from 'lucide-react';

interface RewardAccessCodeCardProps {
    gymId: string;
}

const formatCountdown = (totalSeconds: number) => {
    const s = Math.max(0, totalSeconds);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

export function RewardAccessCodeCard({ gymId }: RewardAccessCodeCardProps) {
    const [data, setData] = useState<RewardAccessCode | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRegenerating, setIsRegenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [secondsLeft, setSecondsLeft] = useState(0);
    const [copied, setCopied] = useState(false);
    const { toast } = useToast();
    const rotatedForRef = useRef<string | null>(null);

    const fetchCode = async (silent = false) => {
        if (!gymId) return;
        if (!silent) setIsLoading(true);
        try {
            const res = await gymApi.getRewardAccessCode(gymId);
            if (!res?.success || !res.code) {
                setError(res?.message || 'No se pudo generar el código.');
                return;
            }
            setData(res);
            setError(null);
        } catch (err) {
            reportError(err, { operation: 'rewards_access_code_get', area: 'crm', extra: { gym_id: gymId } });
            setError('No se pudo cargar el código de acceso.');
        } finally {
            if (!silent) setIsLoading(false);
        }
    };

    useEffect(() => {
        setData(null);
        setError(null);
        setCopied(false);
        rotatedForRef.current = null;
        setIsLoading(true);
        fetchCode();
    }, [gymId]);

    useEffect(() => {
        if (!data?.valid_until) return;

        const tick = () => {
            const ms = new Date(data.valid_until).getTime() - Date.now();
            setSecondsLeft(Math.max(0, Math.ceil(ms / 1000)));

            if (ms <= 0 && rotatedForRef.current !== data.valid_until) {
                rotatedForRef.current = data.valid_until;
                fetchCode(true);
            }
        };

        tick();
        const timer = window.setInterval(tick, 1000);
        return () => window.clearInterval(timer);
    }, [data?.valid_until]);

    useEffect(() => {
        if (!copied) return;
        const timer = window.setTimeout(() => setCopied(false), 2000);
        return () => window.clearTimeout(timer);
    }, [copied]);

    const handleCopy = async () => {
        if (!data?.code) return;
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(data.code);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = data.code;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            setCopied(true);
            toast({ title: 'Código copiado' });
        } catch (err) {
            reportError(err, { operation: 'rewards_access_code_copy', area: 'crm' });
            toast({ title: 'No se pudo copiar', description: 'Copia el código manualmente.', variant: 'destructive' });
        }
    };

    const handleRegenerate = async () => {
        if (!gymId || isRegenerating) return;
        if (!window.confirm('¿Generar un código nuevo? El código actual dejará de funcionar de inmediato.')) return;

        setIsRegenerating(true);
        try {
            const res = await gymApi.regenerateRewardAccessCode(gymId);
            if (!res?.success || !res.code) {
                toast({
                    title: 'No se pudo generar',
                    description: res?.message || 'Intenta de nuevo.',
                    variant: 'destructive',
                });
                return;
            }
            setData(res);
            setError(null);
            setCopied(false);
            rotatedForRef.current = null;
            toast({ title: 'Nuevo código generado', description: 'Compártelo con recepción.' });
        } catch (err) {
            reportError(err, { operation: 'rewards_access_code_regenerate', area: 'crm', extra: { gym_id: gymId } });
            toast({ title: 'Error', description: 'No se pudo generar el código.', variant: 'destructive' });
        } finally {
            setIsRegenerating(false);
        }
    };

    return (
        <Card className="overflow-hidden border-primary/30 bg-gradient-to-br from-primary/10 via-background to-background">
            <CardContent className="p-5 sm:p-6">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary">
                                <KeyRound className="h-5 w-5" />
                            </div>
                            <div>
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground">
                                    Código de acceso de hoy
                                </p>
                                <p className="text-xs text-muted-foreground">
                                    Los socios lo ingresan en su auto check-in para usar sus recompensas.
                                </p>
                            </div>
                        </div>

                        {isLoading ? (
                            <div className="flex items-center gap-3 py-2">
                                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                                <span className="text-sm text-muted-foreground">Cargando código...</span>
                            </div>
                        ) : error ? (
                            <div className="space-y-2 py-1">
                                <p className="text-sm font-bold text-destructive">{error}</p>
                                <Button variant="outline" size="sm" onClick={() => fetchCode()}>
                                    <RefreshCw className="mr-2 h-4 w-4" /> Reintentar
                                </Button>
                            </div>
                        ) : data ? (
                            <div className="flex flex-wrap items-center gap-3">
                                <span className="font-mono text-4xl font-black tracking-[0.35em] text-primary sm:text-5xl">
                                    {data.code}
                                </span>
                                <Badge variant="outline" className="gap-1">
                                    <Clock className="h-3 w-3" />
                                    {formatCountdown(secondsLeft)}
                                </Badge>
                            </div>
                        ) : null}

                        {data?.valid_until && !error && (
                            <p className="text-[11px] text-muted-foreground">
                                Se renueva automáticamente a medianoche (hora del gimnasio). Escríbelo en un lugar visible para tus socios.
                            </p>
                        )}
                    </div>

                    <div className="flex flex-col gap-2 sm:min-w-[160px]">
                        <Button variant="gradient" onClick={handleCopy} disabled={!data?.code || isLoading || !!error}>
                            {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
                            {copied ? 'Copiado' : 'Copiar código'}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={handleRegenerate}
                            disabled={isRegenerating || isLoading || !!error}
                        >
                            {isRegenerating ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <RefreshCw className="mr-2 h-4 w-4" />
                            )}
                            Generar nuevo
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
